import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import useRetailerApi from '@/useRetailerApi'
import useTranslation from '@/hooks/useTranslation'
import { Camera01, XClose } from '@untitledui/icons'
import Button from '@/components/Button'
import OptimizedImage from '@/components/OptimizedImage'
import DatePickerInput from '@/components/DatePickerInput'
import QrScannerModal from '@/components/QrScannerModal'

function CardIcon() {
    return (
        <svg
            width="27"
            height="20"
            viewBox="0 0 27 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M21.6992 18.7C23.0994 18.7 23.7994 18.7 24.3342 18.4197C24.8046 18.1731 25.1871 17.7798 25.4267 17.2959C25.6992 16.7459 25.6992 16.0258 25.6992 14.5857V4.81424C25.6992 3.3741 25.6992 2.65404 25.4267 2.10398C25.1871 1.62013 24.8046 1.22675 24.3342 0.980222C23.7994 0.699953 23.0994 0.699953 21.6992 0.699953L4.69922 0.699951C3.29909 0.699951 2.59902 0.699951 2.06424 0.98022C1.59384 1.22675 1.21139 1.62013 0.971703 2.10398C0.699219 2.65403 0.699219 3.3741 0.699219 4.81424L0.699219 14.5857C0.699219 16.0258 0.699219 16.7459 0.971702 17.2959C1.21139 17.7798 1.59384 18.1731 2.06424 18.4197C2.59902 18.6999 3.29909 18.6999 4.69922 18.6999L21.6992 18.7Z"
                fill="#000D1E"
            />
            <path
                d="M11.9492 12.2714H5.69922M0.699219 4.81424L0.699219 14.5857C0.699219 16.0258 0.699219 16.7459 0.971702 17.2959C1.21139 17.7798 1.59384 18.1731 2.06424 18.4197C2.59902 18.6999 3.29909 18.6999 4.69922 18.6999L21.6992 18.7C23.0994 18.7 23.7994 18.7 24.3342 18.4197C24.8046 18.1732 25.1871 17.7798 25.4267 17.2959C25.6992 16.7459 25.6992 16.0258 25.6992 14.5857V4.81424C25.6992 3.3741 25.6992 2.65404 25.4267 2.10398C25.1871 1.62013 24.8046 1.22675 24.3342 0.980222C23.7994 0.699953 23.0994 0.699953 21.6992 0.699953L4.69922 0.699951C3.29909 0.699951 2.59902 0.699951 2.06424 0.98022C1.59384 1.22675 1.21139 1.62013 0.971703 2.10398C0.699219 2.65403 0.699219 3.3741 0.699219 4.81424Z"
                stroke="white"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

function SelectableCard({ item, selected, onClick }) {
    const buttonClass = clsx(
        'flex flex-col items-center justify-center w-[120px] gap-[12px] px-[12px] lg:px-[7.5px] py-[12px] rounded-[12px] cursor-pointer transition-all',
        selected && 'border border-baltic-blue bg-baltic-blue/5',
        !selected && 'border border-dark-blue/10 hover:bg-gray-50'
    )

    const labelClass = clsx(
        'text-[12px] uppercase leading-[100%] text-center',
        selected && 'text-baltic-blue',
        !selected && 'text-dark-blue/40'
    )

    const nameParts = item.name.split(' - ')

    return (
        <button onClick={onClick} className={buttonClass}>
            <OptimizedImage
                src={item.image_url}
                alt={item.name}
                sizes="86px"
                className="w-[86px] h-[116px] object-contain"
            />

            <span className={labelClass}>
                {nameParts.map((part, i) => (
                    <span key={i}>
                        {i > 0 && <br />}
                        {part}
                    </span>
                ))}
            </span>
        </button>
    )
}

export default function WarrantyForm({ onSubmit }) {
    const { retailer, associateWarranty } = useRetailerApi()
    const { t } = useTranslation()
    const [selectedCollection, setSelectedCollection] = useState(null)
    const [selectedComponent, setSelectedComponent] = useState(null)
    const [saleDate, setSaleDate] = useState(() => {
        const d = new Date()
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    })
    const [cardCode, setCardCode] = useState('WA')
    const [cardCodes, setCardCodes] = useState([])
    const [isScannerOpen, setIsScannerOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState(null)
    const componentsSectionRef = useRef(null)
    const dateSectionRef = useRef(null)
    const cardListRef = useRef(null)

    useEffect(() => {
        if (cardListRef.current) {
            cardListRef.current.scrollTop = cardListRef.current.scrollHeight
        }
    }, [cardCodes])

    const componentsInCollection = selectedCollection
        ? retailer.warranty_collections.find(
              (collection) => collection.id === selectedCollection.id
          )?.components || []
        : []

    const scrollToRef = (ref) => {
        setTimeout(() => {
            const el = ref.current
            if (!el) return
            const offset = window.innerWidth >= 1024 ? 260 : 100
            const top =
                el.getBoundingClientRect().top + window.scrollY - offset
            window.scrollTo({ top, behavior: 'smooth' })
        }, 0)
    }

    const handleSelectCollection = (collection) => {
        setSelectedCollection(collection)
        setSelectedComponent(null)
        scrollToRef(componentsSectionRef)
    }

    const handleSelectComponent = (component) => {
        setSelectedComponent(component)
        scrollToRef(dateSectionRef)
    }

    const handleQrScan = (code) => {
        const match = code.match(/baltic-watches\.com\/w\/(WA[A-Za-z0-9]+)/i)
        if (!match) return false
        const scannedCode = match[1].toUpperCase()
        if (!cardCodes.includes(scannedCode)) {
            setCardCodes((prev) => [...prev, scannedCode])
        }
        return true
    }

    return (
        <>
            <span className="text-[30px] lg:text-[34px] uppercase font-semibold">
                {t('warranty.association')}
            </span>

            <div className="flex flex-col gap-[12px] mt-[66px]">
                <span className="text-[18px] font-semibold uppercase">
                    {t('warranty.chooseCollection')}
                </span>

                <div className="grid grid-cols-[repeat(auto-fill,120px)] gap-[8px] text-center">
                    {retailer.warranty_collections.map((collection) => (
                        <SelectableCard
                            key={collection.id}
                            item={collection}
                            selected={
                                selectedCollection?.id === collection.id
                            }
                            onClick={() =>
                                handleSelectCollection(collection)
                            }
                        />
                    ))}
                </div>
            </div>

            {selectedCollection && (
                <div
                    ref={componentsSectionRef}
                    className="flex flex-col gap-[12px] mt-[240px]"
                >
                    <span className="text-[18px] font-semibold uppercase">
                        {t('warranty.chooseModel')}
                    </span>

                    <div className="grid grid-cols-[repeat(auto-fill,120px)] gap-[8px] text-center">
                        {componentsInCollection.map((component) => (
                            <SelectableCard
                                key={component.id}
                                item={component}
                                selected={
                                    selectedComponent?.id === component.id
                                }
                                onClick={() =>
                                    handleSelectComponent(component)
                                }
                            />
                        ))}
                    </div>
                </div>
            )}

            {selectedComponent && (
                <div
                    ref={dateSectionRef}
                    className="flex flex-col mt-[240px] gap-[80px]"
                >
                    <div className="flex flex-col gap-[16px]">
                        <div className="flex flex-col gap-[8px]">
                            <span className="text-[18px] font-semibold uppercase">
                                {t('warranty.chooseSaleDate')}
                            </span>
                            <span className="text-[14px] text-dark-blue">
                                {t('warranty.saleDateDescription')}
                            </span>
                        </div>

                        <div className="max-w-[630px]">
                            <DatePickerInput
                                value={saleDate}
                                onChange={setSaleDate}
                                placeholder={t(
                                    'warranty.saleDatePlaceholder'
                                )}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-[16px]">
                        <div className="flex flex-col gap-[8px]">
                            <span className="text-[18px] font-semibold uppercase">
                                {t('warranty.cardsToAssociate')}
                            </span>
                            <span className="text-[14px] text-dark-blue">
                                {t('warranty.cardsDescription')}
                            </span>

                            <div className="mt-[16px] flex flex-col lg:flex-row items-stretch lg:items-center gap-[12px] lg:gap-[20px]">
                                <div className="lg:flex-1">
                                    <Button
                                        fullWidth
                                        variant="outline"
                                        icon={<Camera01 size={14} />}
                                        onClick={() =>
                                            setIsScannerOpen(true)
                                        }
                                    >
                                        {t('warranty.scanQrCode')}
                                    </Button>
                                </div>

                                <span className="text-dark-blue-40 text-center">
                                    {t('common.or')}
                                </span>

                                <div className="lg:flex-1 flex items-center justify-between bg-background-product rounded-[8px] h-[44px] pl-[16px] pr-[4px]">
                                    <input
                                        type="text"
                                        value={cardCode}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' &&
                                                cardCode.length >= 7 &&
                                                !cardCodes.includes(
                                                    cardCode
                                                )
                                            ) {
                                                setCardCodes([
                                                    ...cardCodes,
                                                    cardCode,
                                                ])
                                                setCardCode('WA')
                                            }
                                        }}
                                        onChange={(e) => {
                                            let val = e.target.value
                                                .toUpperCase()
                                                .replace(
                                                    /[^A-Z0-9]/g,
                                                    ''
                                                )
                                            if (!val.startsWith('WA'))
                                                val = 'WA'
                                            if (val.length > 10)
                                                val = val.slice(0, 10)
                                            setCardCode(val)
                                        }}
                                        maxLength={10}
                                        className="bg-transparent text-[16px] text-dark-blue outline-none w-full"
                                    />
                                    <button
                                        disabled={cardCode.length < 7}
                                        onClick={() => {
                                            if (
                                                cardCode.length >= 7 &&
                                                !cardCodes.includes(
                                                    cardCode
                                                )
                                            ) {
                                                setCardCodes([
                                                    ...cardCodes,
                                                    cardCode,
                                                ])
                                                setCardCode('WA')
                                            }
                                        }}
                                        className={`font-semibold text-[14px] rounded-[4px] h-[36px] px-[16px] ${cardCode.length >= 7 ? 'bg-dark-blue text-white cursor-pointer' : 'bg-dark-blue-10 text-white/50 cursor-not-allowed'}`}
                                    >
                                        {t('common.add')}
                                    </button>
                                </div>
                            </div>

                            {cardCodes.length > 0 && (
                                <div ref={cardListRef} className="border border-dark-blue/10 rounded-[8px] p-[16px] flex flex-col gap-[10px] w-full max-h-[200px] overflow-y-auto">
                                    {cardCodes.map((code) => (
                                        <div
                                            key={code}
                                            className="flex items-center justify-between"
                                        >
                                            <div className="flex items-center gap-[10px]">
                                                <CardIcon />
                                                <span className="text-[14px] text-dark-blue">
                                                    {code}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() =>
                                                    setCardCodes(
                                                        cardCodes.filter(
                                                            (c) =>
                                                                c !== code
                                                        )
                                                    )
                                                }
                                                className="text-dark-blue/40 hover:text-dark-blue cursor-pointer"
                                            >
                                                <XClose size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {error && (
                                <span className="text-xs text-[#E84341]">
                                    {error}
                                </span>
                            )}

                            {cardCodes.length > 0 && (
                                <Button
                                    fullWidth
                                    loading={isSubmitting}
                                    onClick={async () => {
                                        setError(null)
                                        setIsSubmitting(true)
                                        try {
                                            await associateWarranty({
                                                component_id: selectedComponent.id,
                                                warranty_start_at: saleDate,
                                                codes: cardCodes,
                                            })
                                            onSubmit({
                                                collection: selectedCollection.name,
                                                model: selectedComponent.name,
                                                imageUrl: selectedComponent.image_url,
                                                warrantyStartDate: saleDate,
                                                cardCodes,
                                            })
                                        } catch (err) {
                                            setError(err.error_message || err.message || t('common.error'))
                                        } finally {
                                            setIsSubmitting(false)
                                        }
                                    }}
                                >
                                    {cardCodes.length === 1
                                        ? t('warranty.associateCard')
                                        : t('warranty.associateCards')}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="h-[60vh]" />

            <QrScannerModal
                isOpen={isScannerOpen}
                onClose={() => setIsScannerOpen(false)}
                onScan={handleQrScan}
            />
        </>
    )
}
